import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

// Book benchmarks, used only until a user has their own real data to replace them with:
// - Referred candidates are hired ~55% faster than the 42-day average time-to-hire (Ch. 9/11 sources)
const BENCHMARK_DAYS = { confirmed: 19, other: 42 }

function daysBetween(a, b) {
  return Math.max(0, Math.round((new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24)))
}

function formatDate(d) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function Forecast({ session }) {
  const [loading, setLoading] = useState(true)
  const [result, setResult] = useState(null) // { nearestDate, sampleSize, usingPersonalData, activeCount }

  useEffect(() => { compute() }, [session?.user?.id])

  async function compute() {
    if (!session?.user?.id) { setLoading(false); return }
    setLoading(true)

    const [{ data: items }, { data: history }] = await Promise.all([
      supabase.from('pipeline').select('id, stage, referral_status, created_at').eq('user_id', session.user.id),
      supabase.from('pipeline_stage_history').select('pipeline_id, stage, entered_at').eq('user_id', session.user.id).order('entered_at', { ascending: true }),
    ])

    const itemsData = items || []
    const historyData = history || []

    // Group history entries by pipeline item
    const byItem = {}
    for (const h of historyData) {
      if (!byItem[h.pipeline_id]) byItem[h.pipeline_id] = []
      byItem[h.pipeline_id].push(h)
    }

    // Build completed-cycle samples (items that reached 'offer'), bucketed by referral status
    const completed = { confirmed: [], other: [] }
    for (const item of itemsData) {
      if (item.stage !== 'offer') continue
      const hist = byItem[item.id]
      if (!hist || hist.length === 0) continue
      const first = hist[0].entered_at
      const offerEntry = hist.find(h => h.stage === 'offer')
      if (!offerEntry) continue
      const cycle = daysBetween(first, offerEntry.entered_at)
      const bucket = item.referral_status === 'confirmed' ? 'confirmed' : 'other'
      completed[bucket].push(cycle)
    }

    const avg = arr => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null
    const personalAvg = { confirmed: avg(completed.confirmed), other: avg(completed.other) }
    const usingPersonalData = completed.confirmed.length > 0 || completed.other.length > 0

    function expectedCycleFor(item) {
      const bucket = item.referral_status === 'confirmed' ? 'confirmed' : 'other'
      return personalAvg[bucket] != null ? personalAvg[bucket] : BENCHMARK_DAYS[bucket]
    }

    // Project forward for every still-active item
    const active = itemsData.filter(i => i.stage !== 'offer' && i.stage !== 'rejected')
    const projections = active.map(item => {
      const hist = byItem[item.id]
      const first = hist && hist.length ? hist[0].entered_at : item.created_at
      const expectedCycle = expectedCycleFor(item)
      const elapsed = daysBetween(first, new Date().toISOString())
      const daysRemaining = Math.max(expectedCycle - elapsed, 1)
      const projectedDate = new Date()
      projectedDate.setDate(projectedDate.getDate() + daysRemaining)
      return projectedDate
    })

    if (projections.length === 0) {
      setResult({ activeCount: 0 })
      setLoading(false)
      return
    }

    const nearestDate = new Date(Math.min(...projections.map(d => d.getTime())))
    setResult({
      nearestDate,
      sampleSize: completed.confirmed.length + completed.other.length,
      usingPersonalData,
      activeCount: active.length,
    })
    setLoading(false)
  }

  if (loading) return null

  if (!result || result.activeCount === 0) {
    return (
      <div className="card" style={{ background: '#F5F5F5', border: '1px dashed #ccc', textAlign: 'center', padding: '1.25rem' }}>
        <div style={{ fontSize: '0.82rem', color: '#888' }}>Add an opportunity to your Pipeline to see your offer forecast.</div>
      </div>
    )
  }

  return (
    <div className="card" style={{ background: '#E8F2EC', borderLeft: '4px solid #00712D', borderRadius: '0 10px 10px 0' }}>
      <div className="eyebrow" style={{ marginBottom: '0.35rem' }}>Your Offer Forecast</div>
      <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.3rem', fontWeight: 700, color: '#1A1A1A', marginBottom: '0.35rem' }}>
        Around {formatDate(result.nearestDate)}
      </div>
      <p style={{ fontSize: '0.8rem', color: '#555', lineHeight: 1.6 }}>
        {result.usingPersonalData
          ? `Based on your own pipeline history (${result.sampleSize} completed ${result.sampleSize === 1 ? 'cycle' : 'cycles'}) across ${result.activeCount} active ${result.activeCount === 1 ? 'opportunity' : 'opportunities'}.`
          : `Based on referral-hire benchmarks across your ${result.activeCount} active ${result.activeCount === 1 ? 'opportunity' : 'opportunities'} — this will get more accurate as you log real outcomes.`}
      </p>
      <p style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.5rem', fontStyle: 'italic' }}>
        An estimate, not a guarantee — keep the pipeline full either way.
      </p>
    </div>
  )
}
